import {
    ref,
    computed,
    readonly
} from 'vue'

export type NotificationType = 'success' | 'info' | 'warning' | 'danger'

export interface NotificationOptions {
    title: string
    text: string
    type?: NotificationType
    delay?: number
    closable?: boolean
}

export interface Notification extends NotificationOptions {
    id: number
    createdAt: number
}

let notificationInstance: ReturnType<typeof createNotificationService>

function createNotificationService() {
    const _notifications = ref<Notification[]>([])
    const MAX_NOTIFICATIONS = 6
    let nextId = 0

    const notifications = computed(() =>
        _notifications.value.slice().sort((a, b) => b.createdAt - a.createdAt)
    )

    const count = computed(() => _notifications.value.length)
    const hasNotifications = computed(() => _notifications.value.length > 0)

    const show = (options: NotificationOptions): number => {
        const id = ++nextId
        const createdAt = Date.now()

        const notification: Notification = {
            id,
            createdAt,
            title: options.title,
            text: options.text,
            type: options.type || 'info',
            delay: options.delay ?? 2000,
            closable: options.closable ?? true
        }

        _notifications.value.unshift(notification)

        if (_notifications.value.length > MAX_NOTIFICATIONS) {
            _notifications.value.pop()
        }

        if (notification.delay! > 0) {
            setTimeout(() => {
                remove(id)
            }, notification.delay)
        }

        return id
    }

    const remove = (id: number): void => {
        const index = _notifications.value.findIndex(n => n.id === id)
        if (index !== -1) {
            _notifications.value.splice(index, 1)
        }
    }

    const clear = (): void => {
        _notifications.value = []
    }

    const success = (title: string, text: string, delay?: number) =>
        show({ title, text, type: 'success', delay })

    const info = (title: string, text: string, delay?: number) =>
        show({ title, text, type: 'info', delay })

    const warning = (title: string, text: string, delay?: number) =>
        show({ title, text, type: 'warning', delay })

    const danger = (title: string, text: string, delay?: number) =>
        show({ title, text, type: 'danger', delay })

    return {
        notifications: readonly(notifications),
        count,
        hasNotifications,
        show,
        remove,
        clear,
        success,
        info,
        warning,
        danger
    }
}

export function useNotification() {
    if (!notificationInstance) {
        notificationInstance = createNotificationService()
    }
    return notificationInstance
}
